import {
  collection,
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  getDocs,
  getCountFromServer,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  where,
} from 'firebase/firestore';

import { db } from '../firebase';
import {
  getCanonicalTeamName,
  getTeamSectionQueryValues,
} from '../config/teamAliases';
import {
  buildLegacyTagCandidates,
  getSearchTerms,
  matchesNewsSearch,
  normalizeSearchText,
} from '../utils/searchText';

const newsCollection = collection(db, 'news');

const transliterationMap = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'h',
  ґ: 'g',
  д: 'd',
  е: 'e',
  є: 'ye',
  ж: 'zh',
  з: 'z',
  и: 'y',
  і: 'i',
  ї: 'yi',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ь: '',
  ю: 'yu',
  я: 'ya',
};

const createSlug = (title = '') => {
  return title
    .toLowerCase()
    .split('')
    .map((character) => transliterationMap[character] ?? character)
    .join('')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’'`"«»]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 140);
};

export const fetchHomeNews = async (limitCount = 80) => {
  const q = query(
    newsCollection,
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const loadCategoryNews = async (category, limitCount = 30) => {
  if (!category) return [];

  const q = query(
    newsCollection,
    where('category', '==', category),
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const getCategoryNewsCount = async (category) => {
  if (!category) return 0;

  const q = query(
    newsCollection,
    where('category', '==', category)
  );

  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

export const loadSectionNews = async (section, limitCount = 24) => {
  if (!section) return [];

  const sectionValues = getTeamSectionQueryValues(section);
  const q = query(
    newsCollection,
    sectionValues.length > 1
      ? where('sections', 'array-contains-any', sectionValues)
      : where('sections', 'array-contains', sectionValues[0]),
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const getSectionNewsCount = async (section) => {
  if (!section) return 0;

  const sectionValues = getTeamSectionQueryValues(section);
  const q = query(
    newsCollection,
    sectionValues.length > 1
      ? where('sections', 'array-contains-any', sectionValues)
      : where('sections', 'array-contains', sectionValues[0])
  );

  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

export const addNewsToFirestore = async (newNews) => {
  const slug = createSlug(newNews.title);

  const timestampInSeconds = Math.floor(Date.now() / 1000);

  const customId = `${timestampInSeconds}-${slug}.html`;

  const publishedAt =
    newNews.publishedAt || new Date();

  const newsWithTimestamp = {
    ...newNews,
    slug,
    timestamp: serverTimestamp(),
    publishedAt,
  };

  const newsDoc = doc(
    db,
    'news',
    customId
  );

  await setDoc(
    newsDoc,
    newsWithTimestamp
  );

  return {
    id: customId,
    ...newNews,
    slug,
    timestamp: {
      seconds: timestampInSeconds,
    },
    publishedAt,
  };
};

export const updateNewsInFirestore = async (updatedNews) => {
  const { id, ...data } = updatedNews;
  const newsDoc = doc(db, 'news', id);

  await updateDoc(newsDoc, data);

  return updatedNews;
};

export const deleteNewsFromFirestore = async (id) => {
  await deleteDoc(doc(db, 'news', id));
  return id;
};

export const searchNewsByTag = async (tag, limitCount = 30) => {
  const normalizedQuery = normalizeSearchText(tag);
  const terms = getSearchTerms(normalizedQuery);
  if (terms.length === 0) return [];

  const candidateLimit = Math.min(Math.max(limitCount * 4, 100), 500);
  const legacyCandidates = buildLegacyTagCandidates(normalizedQuery);
  const normalizedQueryRequest = getDocs(query(
    newsCollection,
    where('searchTokens', 'array-contains', terms[0]),
    limit(candidateLimit)
  )).catch((error) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Normalized search query unavailable:', error);
    }
    return null;
  });
  const legacyQueryRequest = legacyCandidates.length > 0
    ? getDocs(query(
      newsCollection,
      where('tags', 'array-contains-any', legacyCandidates),
      orderBy('publishedAt', 'desc'),
      limit(candidateLimit)
    ))
    : Promise.resolve(null);
  const [normalizedSnapshot, legacySnapshot] = await Promise.all([
    normalizedQueryRequest,
    legacyQueryRequest,
  ]);
  const uniqueNews = new Map();

  [normalizedSnapshot, legacySnapshot].forEach((snapshot) => {
    snapshot?.docs.forEach((document) => {
      const news = { id: document.id, ...document.data() };
      if (matchesNewsSearch(news, normalizedQuery)) {
        uniqueNews.set(document.id, news);
      }
    });
  });

  return [...uniqueNews.values()]
    .sort((left, right) => {
      const leftMillis = left.publishedAt?.toMillis?.()
        || left.publishedAt?.seconds * 1000
        || 0;
      const rightMillis = right.publishedAt?.toMillis?.()
        || right.publishedAt?.seconds * 1000
        || 0;
      return rightMillis - leftMillis;
    })
    .slice(0, limitCount);
};

export const getSearchNewsCount = async (tag) => {
  const results = await searchNewsByTag(tag, 500);
  return results.length;
};

export const loadPopularNews = async (limitCount = 20) => {
  const q = query(
    newsCollection,
    orderBy('views', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const loadAdminNewsPage = async ({
  pageSize = 10,
  cursor = null,
} = {}) => {
  const constraints = [
    orderBy('publishedAt', 'desc'),
  ];

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  constraints.push(limit(pageSize));

  const q = query(newsCollection, ...constraints);

  const snapshot = await getDocs(q);

  return {
    news: snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })),
    lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
    hasNext: snapshot.docs.length === pageSize,
  };
};

// Повний список залишається лише для окремого рейтингу авторів.
// Адмінська таблиця використовує loadAdminNewsPage.
export const loadAdminNews = async () => {
  const snapshot = await getDocs(query(
    newsCollection,
    orderBy('publishedAt', 'desc')
  ));

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const loadAllNews = async (limitCount = 30) => {
  const q = query(
    newsCollection,
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const getAllNewsCount = async () => {
  const snapshot = await getCountFromServer(newsCollection);
  return snapshot.data().count;
};

export const loadRelatedNews = async (currentNews, limitCount = 12) => {
  if (!currentNews) return [];

  const firstSection = Array.isArray(currentNews.sections)
    ? getCanonicalTeamName(currentNews.sections[0])
    : null;

  let q;

  if (firstSection) {
    q = query(
      newsCollection,
      where('sections', 'array-contains', firstSection),
      orderBy('publishedAt', 'desc'),
      limit(limitCount)
    );
  } else if (currentNews.category) {
    q = query(
      newsCollection,
      where('category', '==', currentNews.category),
      orderBy('publishedAt', 'desc'),
      limit(limitCount)
    );
  } else {
    return [];
  }

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((d) => ({
      id: d.id,
      ...d.data(),
    }))
    .filter((item) => item.id !== currentNews.id)
    .slice(0, 8);
};
